import { useState, type FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于我们 - 拳皇档案馆',
  description: '拳皇档案馆是一个由格斗游戏爱好者创建的角色攻略社区，致力于为玩家提供最全面、最专业的SNK角色深度解析内容。',
  url: 'https://maishiranui-guide.com/about',
  inLanguage: 'zh-CN',
};

const orgSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '拳皇档案馆',
  url: 'https://maishiranui-guide.com/',
  description: '专注于SNK格斗游戏角色攻略与深度解析的粉丝社区',
  foundingDate: '2024',
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer support',
    email: 'contact@maishiranui-guide.com',
  },
};

const stats = [
  { value: '50+', label: '深度攻略文章' },
  { value: '10万+', label: '月活跃读者' },
  { value: '5', label: '核心内容分类' },
  { value: '30+', label: '覆盖游戏作品' },
];

const team = [
  {
    name: '格斗老杨',
    role: '创始人 & 主编',
    desc: '拳皇系列20年老玩家，曾参与多项国内格斗赛事解说。致力于将最专业的格斗知识传递给新一代玩家。',
  },
  {
    name: '小舞研究员',
    role: '内容总监',
    desc: '不知火舞角色专精玩家，全系列舞使用率超过80%。擅长帧数分析与连招研发，负责本站核心攻略内容质量把控。',
  },
  {
    name: 'SNK考古队',
    role: '历史考据',
    desc: '专注于SNK游戏历史研究与角色背景挖掘。从饿狼传说到拳皇最新作，确保每一篇角色解析都有据可查。',
  },
  {
    name: '天梯向导',
    role: '实战教练',
    desc: '拳皇15天梯宗师段位选手，擅长将职业级打法转化为普通玩家可理解的实战教学，负责连招攻略与对战策略内容。',
  },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      return;
    }

    setFormStatus('sending');
    setFormError('');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt') {
          params.append(key, String(value));
        }
      });

      const response = await fetch('https://readdy.ai/api/form/d9q5khnnahb7382ov6j0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (serverMsg && (serverMsg.includes('spam') || serverMsg.includes('form data is spam'))) {
          setFormError(serverMsg);
        } else {
          setFormError(serverMsg || '提交失败，请稍后再试');
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <SchemaOrg data={aboutSchema} />
      <SchemaOrg data={orgSchema} />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          <section className="w-full bg-background-50 py-14 md:py-20">
            <div className="w-full max-w-4xl mx-auto px-4 md:px-6 text-center">
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900 mb-6">
                关于拳皇档案馆
              </h1>
              <p className="text-base md:text-lg text-foreground-600 leading-relaxed max-w-2xl mx-auto">
                我们是一群热爱SNK格斗游戏的玩家，致力于为全球华语玩家提供最全面、最专业的角色攻略与深度解析内容。
              </p>
            </div>
          </section>

          <section className="w-full bg-background-100 py-12 md:py-16">
            <div className="w-full max-w-5xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {stats.map((stat) => (
                  <div key={stat.label} className="text-center p-6 rounded-xl bg-background-50 border border-background-200/70">
                    <div className="font-heading text-3xl md:text-4xl font-bold text-primary-600 mb-2">
                      {stat.value}
                    </div>
                    <div className="text-sm text-foreground-500">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="w-full bg-background-50 py-14 md:py-20">
            <div className="w-full max-w-4xl mx-auto px-4 md:px-6">
              <div className="mb-12">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                  我们的故事
                </h2>
                <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                  <p>
                    拳皇档案馆成立于2024年，由资深格斗游戏玩家「格斗老杨」发起。彼时，华语互联网上关于SNK角色——尤其是不知火舞——的系统性攻略内容极为稀缺，
                    大多分散在各个论坛和视频平台的碎片化讨论中。我们意识到，需要一个集中的、高质量的、持续更新的中文攻略平台，让新老玩家都能找到可靠的参考。
                  </p>
                  <p>
                    从最初的一篇文章开始，拳皇档案馆逐步发展为一个涵盖角色背景、技能体系、连招攻略、文化影响等多维度的内容平台。
                    我们的团队由资深玩家、帧数研究者、格斗教练和历史考据爱好者组成，每一位成员都怀着对SNK游戏世界最真挚的热爱。
                  </p>
                  <p>
                    本站内容始终坚持「玩家写给玩家」的理念——所有攻略都经过实战验证，所有背景考据都有据可查。
                    我们不追求标题党式的流量，只希望每一位来到这里的玩家，都能找到对自己有帮助的内容，在格斗游戏的世界中不断进步。
                  </p>
                </div>
              </div>

              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-8">
                  核心团队
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {team.map((member) => (
                    <div
                      key={member.name}
                      className="p-6 rounded-xl bg-background-50 border border-background-200/70"
                    >
                      <div className="flex items-center gap-4 mb-3">
                        <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-500 text-background-50 font-heading font-bold text-lg">
                          {member.name.charAt(0)}
                        </div>
                        <div>
                          <h3 className="font-heading font-semibold text-foreground-900 text-sm">
                            {member.name}
                          </h3>
                          <p className="text-xs text-primary-600">{member.role}</p>
                        </div>
                      </div>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {member.desc}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section id="contact" className="w-full bg-background-100 py-14 md:py-20">
            <div className="w-full max-w-3xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                  联系我们
                </h2>
                <p className="text-sm text-foreground-600">
                  如果您有任何问题、建议或合作意向，欢迎通过以下方式与我们取得联系
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-12">
                <div className="text-center p-6 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-100/70 text-primary-600 mb-3">
                    <i className="ri-mail-line text-xl"></i>
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-1">电子邮件</h3>
                  <p className="text-xs text-foreground-500">contact@maishiranui-guide.com</p>
                </div>
                <div className="text-center p-6 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-100/70 text-accent-600 mb-3">
                    <i className="ri-weibo-line text-xl"></i>
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-1">微博</h3>
                  <p className="text-xs text-foreground-500">@拳皇档案馆</p>
                </div>
                <div className="text-center p-6 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-secondary-100/70 text-secondary-600 mb-3">
                    <i className="ri-discord-line text-xl"></i>
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-1">Discord</h3>
                  <p className="text-xs text-foreground-500">discord.gg/kof-archives</p>
                </div>
              </div>

              <form
                onSubmit={handleSubmit}
                className="bg-background-50 rounded-xl border border-background-200/70 p-6 md:p-8"
                data-readdy-form=""
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      您的称呼
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300/60 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200/50 transition-all"
                      placeholder="请输入您的称呼"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      电子邮箱
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300/60 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200/50 transition-all"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>
                <div className="mb-5">
                  <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    required
                    className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300/60 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200/50 transition-all"
                    placeholder="请输入主题"
                  />
                </div>
                <div className="mb-5">
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    maxLength={500}
                    required
                    className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300/60 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200/50 transition-all resize-none"
                    placeholder="请输入您的留言（最多500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1 text-right">最多 500 字符</p>
                </div>
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
                <button
                  type="submit"
                  disabled={formStatus === 'sending'}
                  className="w-full sm:w-auto px-8 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {formStatus === 'sending' ? '发送中...' : '发送留言'}
                  <i className="ri-send-plane-line ml-2"></i>
                </button>

                {formStatus === 'success' && (
                  <div className="mt-4 p-4 rounded-lg bg-accent-100/70 border border-accent-300/60 text-sm text-accent-800">
                    <i className="ri-check-line mr-2"></i>
                    感谢您的留言！我们会尽快回复您。
                  </div>
                )}

                {formStatus === 'error' && formError && (
                  <div className="mt-4 p-4 rounded-lg bg-primary-100/70 border border-primary-300/60 text-sm text-primary-700">
                    <i className="ri-error-warning-line mr-2"></i>
                    {formError}
                  </div>
                )}
              </form>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}